import type { UseQueryReturnType } from '@tanstack/vue-query';
import { httpGet } from '@/api/http';
import { V2_CONTACT_AVATAR, V2_CONTACTS } from '@/constants/endpoint';
import type { ContactUrl } from '@/data/contacts/ContactUrl';
import type { ContactPhone } from '@/data/contacts/ContactPhone';
import type { ContactAttachment } from '@/data/contacts/ContactAttachment';
import type { ContactAddress } from '@/data/contacts/ContactAddress';
import type { ContactEmail } from '@/data/contacts/ContactEmail';
import { byContactUuidMapping, useUsers } from '@/api/useUsers';
import type { Contact, ContactExtended } from '@/data/contacts/Contact';
import type { User } from '@/data/User';
import type { AttachmentDTO } from '@/data/Attachment';
import { useFavorites } from '@/api/useFavorites';
import { computed } from 'vue';
import type { AxiosError } from 'axios';
import { useAuthenticatedQuery } from '@/utils/query';
import { isOutlookContact, useOutlookContactsStore } from '@/stores/useOutlookContactsStore';
import { isElectron } from '@/utils/clientUtils';

export const CONTACTS_QUERY_KEY = 'contacts';

export const contactsQuery = {
  queryKey: [CONTACTS_QUERY_KEY],
  queryFn: () =>
    httpGet<{ contacts: ContactDTO[] }>(V2_CONTACTS)
      .then((r) => r.contacts)
      .then((r) => r.map(mapContactDTOToContactData)),
  refetchInterval: 600_000,
  refetchOnMount: false,
};
// need to explicitly type due to issue
const _useContacts = (): UseQueryReturnType<Contact[] | undefined, AxiosError<unknown, any>> => {
  return useAuthenticatedQuery(contactsQuery);
};

export const useContacts = () => {
  const contacts = _useContacts();
  const favorites = useFavorites();
  const { data: users } = useUsers(byContactUuidMapping);
  const $outlookContactsStore = useOutlookContactsStore();

  const mergedContacts = computed(() => {
    if (!isElectron()) return contacts.data.value || [];
    return [...(contacts.data.value || []), ...$outlookContactsStore._outlookContacts];
  });

  const mappedContacts = computed(() => {
    return mergedContacts.value.map((contactData) => mapToExtendedContact(contactData, users.value, favorites.data?.value));
  });

  return {
    data: mappedContacts,
    isLoading: contacts.isLoading,
    isError: contacts.isError,
    refetch: contacts.refetch,
  };
};

export const mapToExtendedContact = (
  contactData: Contact,
  users: Map<string, User> | undefined,
  favorites: Map<string, { contact_uuid: string }> | undefined
): ContactExtended => {
  const user = contactData.type === 'user' ? users?.get(contactData.uuid) : undefined;
  const isFavorite = isOutlookContact(contactData) ? !!contactData.isFavorite : !!favorites?.get(contactData.uuid);

  return {
    uuid: contactData.uuid,
    url: contactData.url,
    type: contactData.type,
    title: contactData.title,
    timeZone: contactData.timeZone,
    role: contactData.role,
    organization: contactData.organization,
    note: contactData.note,
    nickname: contactData.nickname,
    namePrefix: contactData.namePrefix,
    nameGiven: contactData.nameGiven,
    nameMiddle: contactData.nameMiddle,
    nameFamily: contactData.nameFamily,
    nameSuffix: contactData.nameSuffix,
    category: contactData.category,
    phones: contactData.phones,
    attachments: contactData.attachments,
    addresses: contactData.addresses,
    department: contactData.department,
    costCenter: contactData.costCenter,
    emails: contactData.emails,
    urls: contactData.urls,
    isOutlookContact: contactData.isOutlookContact,
    // new properties
    isFavorite,
    extension: user?.extension?.extension,
    phoneNumber: getPhoneNumber(contactData, user),
    name: getName(contactData, user),
    initials: getInitials(contactData, user),
    user,
    source: 'Api',
    profilePicture: getProfilePictureURL(contactData),
    personalPhonebookGroupUuids: contactData.personalPhonebookGroupUuids,
    globalPhonebookGroupUuids: contactData.globalPhonebookGroupUuids,
  } as ContactExtended;
};

const getPhoneNumber = (contact: Contact, user?: User) => {
  if (user?.extension?.extension) {
    return user.extension.extension;
  }
  // is it possible that the only number is not primary?
  return contact.phones.find((phone) => phone.primary)?.number || contact.phones[0]?.number;
};

const getName = (contact: Contact, user?: User): string => {
  const familyPart = [contact.nameFamily, contact.nameSuffix].filter(Boolean).join(' ');
  const givenPart = [contact.nameGiven, contact.nameMiddle].filter(Boolean).join(' ');
  const prefixPart = contact.namePrefix;

  const nameParts = [familyPart, givenPart, prefixPart].filter(Boolean);
  const fullName = nameParts.join(', ');

  return fullName || contact.nickname || user?.username || '?';
};

const getInitials = (contact: Contact, user?: User): string => {
  // Get non-empty name parts and map to their first letters
  const initials = [contact.nameGiven, contact.nameMiddle, contact.nameFamily]
    .filter((namePart): namePart is string => !!namePart) // Type narrowing
    .map((namePart) => namePart[0]);

  // If no initials were found, return the first letter of the username or '?'
  if (initials.length === 0) {
    return user?.username?.[0] || '?';
  }

  // Combine the first and last initials
  const firstInitial = initials[0] as string;
  const lastInitial = initials.length > 1 ? (initials[initials.length - 1] as string) : '';

  return firstInitial + lastInitial;
};

const getProfilePictureURL = (contactData: Contact): string | null => {
  let uuid = contactData.attachments.find((a) => a.primary)?.uuid || contactData.attachments[0];
  if (typeof uuid === 'object') uuid = uuid.uuid;
  return uuid ? `${V2_CONTACT_AVATAR}/${uuid}` : null;
};

export function mapContactDTOToContactData(contactDTO: ContactDTO): Contact {
  return {
    uuid: contactDTO.uuid!,
    url: contactDTO.url,
    type: contactDTO.type,
    title: contactDTO.title,
    timeZone: contactDTO.time_zone,
    role: contactDTO.role,
    organization: contactDTO.organization,
    note: contactDTO.note,
    nickname: contactDTO.nickname,
    namePrefix: contactDTO.name_prefix,
    nameGiven: contactDTO.name_given,
    nameMiddle: contactDTO.name_middle,
    nameFamily: contactDTO.name_family,
    nameSuffix: contactDTO.name_suffix,
    category: contactDTO.category,
    phones: contactDTO.phones.map(mapPhoneDTOToContactPhone),
    attachments: contactDTO.attachments.map(mapAttachmentDTOToContactAttachment),
    addresses: contactDTO.addresses.map(mapAddressDTOToContactAddress),
    emails: contactDTO.emails.map(mapEmailDTOToContactEmail),
    urls: contactDTO.urls,
    isOutlookContact: false,
    personalPhonebookGroupUuids: contactDTO.personal_phonebook_group_uuids,
    globalPhonebookGroupUuids: contactDTO.global_phonebook_group_uuids,
    department: contactDTO.department,
    costCenter: contactDTO.cost_center,
  };
}

function mapPhoneDTOToContactPhone(phoneDTO: PhoneDTO): ContactPhone {
  return {
    uuid: phoneDTO.uuid!,
    description: phoneDTO.description,
    extension: phoneDTO.extension,
    label: phoneDTO.label,
    number: phoneDTO.number,
    primary: phoneDTO.primary,
    speedDial: phoneDTO.speed_dial,
    typeFax: phoneDTO.type_fax,
    typeText: phoneDTO.type_text,
    typeVideo: phoneDTO.type_video,
    typeVoice: phoneDTO.type_voice,
  };
}

function mapAttachmentDTOToContactAttachment(attachmentDTO: AttachmentDTO): ContactAttachment {
  return {
    uuid: attachmentDTO.uuid,
    filename: attachmentDTO.filename,
    primary: attachmentDTO.primary,
    uploadedDate: attachmentDTO.uploaded_date,
    uploadedUserUuid: attachmentDTO.uploaded_user_uuid,
  };
}

function mapAddressDTOToContactAddress(addressDTO: AddressDTO): ContactAddress {
  return {
    uuid: addressDTO.uuid!,
    community: addressDTO.community,
    country: addressDTO.country,
    description: addressDTO.description,
    extended: addressDTO.extended,
    label: addressDTO.label,
    latitude: addressDTO.latitude,
    longitude: addressDTO.longitude,
    locality: addressDTO.locality,
    postalCode: addressDTO.postal_code,
    primary: addressDTO.primary,
    region: addressDTO.region,
    street: addressDTO.street,
    type: addressDTO.type,
  };
}

function mapEmailDTOToContactEmail(emailDTO: EmailDTO): ContactEmail {
  return {
    uuid: emailDTO.uuid!,
    address: emailDTO.address,
    description: emailDTO.description,
    label: emailDTO.label,
    primary: emailDTO.primary,
  };
}

export type ContactType = 'user' | 'personal' | 'global' | 'azure';

export interface ContactDTO {
  uuid: string | null;
  domain_uuid: string;
  type: ContactType;
  organization: string;
  name_prefix: string | null;
  name_given: string;
  name_middle: string | null;
  name_family: string;
  name_suffix: string | null;
  nickname: string;
  title: string | null;
  role: string | null;
  category: string | null;
  url: string | null;
  time_zone: string | null;
  note: string | null;
  last_mod_date: string;
  user_uuid: string;
  phones: PhoneDTO[];
  addresses: AddressDTO[];
  department: string;
  cost_center: string;
  emails: EmailDTO[];
  urls: ContactUrl[];
  attachments: AttachmentDTO[];
  personal_phonebook_group_uuids: string[];
  global_phonebook_group_uuids: string[];
}

export interface PhoneDTO {
  uuid: string | null;
  contact_uuid: string;
  type_voice: boolean;
  type_fax: boolean;
  type_video: boolean;
  type_text: boolean;
  label: string;
  primary: boolean;
  number: string;
  extension: string | null;
  speed_dial: string | null;
  description: string | null;
}

export interface EmailDTO {
  uuid: string | null;
  contact_uuid: string;
  label?: string;
  primary: boolean;
  address: string;
  description?: string;
}

export interface AddressDTO {
  uuid: string | null;
  contact_uuid: string;
  type: string;
  label: string;
  primary: boolean;
  street: string;
  extended?: string;
  community?: string;
  locality: string;
  region?: string;
  postal_code: string;
  country: string;
  latitude?: string;
  longitude?: string;
  description?: string;
}
